/*
 * <option-rows> — renders one product option as full-width rows.
 *
 * The rows are a facade over the theme's own variant picker, which stays in the
 * DOM but hidden: clicking a row clicks the matching native input, so price,
 * availability, media, history and the Intelligems price hooks all run through
 * the theme's normal variant path. Nothing here writes a variant id directly.
 *
 * Buttons in a radiogroup rather than real radios, for the same reason as the
 * pack selector: the row carries a value and a note side by side, which a
 * styled radio label makes awkward. Roving tabindex plus arrow keys gives back
 * the semantics a native radiogroup would have had.
 */

/* A template can carry one of these blocks per product option - socks use two,
   for gender and colour - and each renders this script. A bare class
   declaration then collides on the second parse and throws, so the whole file
   is scoped and guarded. */
;(function () {
  if (customElements.get('option-rows')) return

  class OptionRows extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return
      this.initialised = true
  
      this.rows = Array.from(this.querySelectorAll('[data-option-value]'))
      this.optionName = this.dataset.optionName || ''
      if (!this.rows.length || !this.optionName) return
  
      this.rows.forEach((row) => {
        row.addEventListener('click', () => this.select(row.dataset.optionValue))
        row.addEventListener('keydown', (event) => this.onKeydown(event))
      })
  
      /* The theme can change the variant by other means — a colour swatch, a
         ?variant= link, the back button — so follow the native picker rather
         than assuming these rows are the only way in. */
      const picker = document.querySelector('[data-variant-picker]')
      if (picker) {
        picker.addEventListener('change', () => this.syncFromPicker())
        this.syncFromPicker()
      }
  
      this.paintSwatches()
    }
  
    /* Colour swatches copy their paint from the theme's own hidden picker, which
       the theme fills in from the merchant's swatch settings. Reading it back
       means this control can never disagree with the rest of the site, and a
       colour added in the admin needs no change here.
  
       The theme paints those labels in its own connectedCallback, so retry for a
       few frames rather than reading an unpainted swatch. */
    paintSwatches(attempt = 0) {
      const swatches = this.querySelectorAll('.m-size-rows__swatch')
      if (!swatches.length) return
  
      const field = this.field
      if (!field) {
        if (attempt < 30) requestAnimationFrame(() => this.paintSwatches(attempt + 1))
        return
      }
  
      let painted = 0
      this.rows.forEach((row) => {
        const target = row.querySelector('.m-size-rows__swatch')
        if (!target) return
  
        const node = field.querySelector(`[data-value="${CSS.escape(row.dataset.optionValue)}"]`)
        const source = node && node.querySelector('label')
        if (!source) return
  
        const style = getComputedStyle(source)
        const fill = style.backgroundColor
        const image = style.backgroundImage
  
        if (image && image !== 'none') {
          target.style.backgroundImage = image
          painted += 1
        }
        if (fill && !/rgba\(0, 0, 0, 0\)|transparent/.test(fill)) {
          target.style.setProperty('--m-size-swatch-fill', fill)
          painted += 1
        }
      })
  
      if (!painted && attempt < 30) requestAnimationFrame(() => this.paintSwatches(attempt + 1))
    }
  
    get field() {
      const picker = document.querySelector('[data-variant-picker]')
      if (!picker) return null
      return picker.querySelector(`[data-option-name="${CSS.escape(this.optionName)}"]`)
    }
  
    select(value) {
      if (!value) return
      const field = this.field
      if (!field) return
  
      const select = field.querySelector('select')
      if (select) {
        if (select.value !== value) {
          select.value = value
          select.dispatchEvent(new Event('change', {bubbles: true}))
        }
      } else {
        const node = field.querySelector(`[data-value="${CSS.escape(value)}"]`)
        const input = node && node.querySelector('input[type="radio"]')
        if (input && !input.checked) input.click()
      }
  
      this.paint(value)
    }
  
    /* Read the truth back off the native picker instead of trusting our own
       click — if the theme rejected the combination, the rows should show what
       is actually selected. */
    syncFromPicker() {
      const field = this.field
      if (!field) return
  
      const select = field.querySelector('select')
      const value = select
        ? select.value
        : (field.querySelector('input[type="radio"]:checked') || {}).value ||
          field.getAttribute('data-selected-value')
  
      if (value) this.paint(value)
    }
  
    paint(value) {
      // A label ending in a colon names what is selected, so keep it current.
      const root = this.closest('.m-size-rows')
      const selected = root && root.querySelector('[data-option-selected]')
      if (selected) selected.textContent = value

      this.rows.forEach((row) => {
        const on = row.dataset.optionValue === value
        row.classList.toggle('is-selected', on)
        row.setAttribute('aria-checked', on ? 'true' : 'false')
        row.tabIndex = on ? 0 : -1
      })
    }
  
    onKeydown(event) {
      const keys = ['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft']
      if (!keys.includes(event.key)) return
      event.preventDefault()
  
      const current = this.rows.findIndex((row) => row.classList.contains('is-selected'))
      const forward = event.key === 'ArrowDown' || event.key === 'ArrowRight'
      const next = this.rows[(current + (forward ? 1 : -1) + this.rows.length) % this.rows.length]
  
      if (next) {
        this.select(next.dataset.optionValue)
        next.focus()
      }
    }
  }
  
  customElements.define('option-rows', OptionRows)
})()
